import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import '../styles/pages/JobDetailPage.css';

const JobDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    linkedin_url: '',
    portfolio_url: '',
    current_position: '',
    current_company: '',
    years_of_experience: '',
    expected_salary: '',
    availability: '',
    cover_letter: ''
  });

  useEffect(() => {
    fetchJob();
  }, [slug]);

  const fetchJob = async () => {
    try {
      const response = await api.getJob(slug);
      if (response.success) {
        setJob(response.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, resume: 'Please upload a PDF or Word document' }));
        e.target.value = '';
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: 'File size must be less than 5MB' }));
        e.target.value = '';
        return;
      }
      
      setResumeFile(file);
      setErrors(prev => ({ ...prev, resume: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.cover_letter.trim()) newErrors.cover_letter = 'Cover letter is required';
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    
    try {
      // Create FormData object for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('job_posting_id', job.id);
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append resume file if selected
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }
      
      const response = await api.applyForJob(formDataToSend);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/careers');
        }, 3000);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="container text-center py-5">
          <i className="fas fa-spinner fa-spin fa-3x"></i>
          <p className="mt-3">Loading job details...</p>
        </div>
      </PageWrapper>
    );
  }

  if (!job) {
    return (
      <PageWrapper>
        <div className="container text-center py-5">
          <i className="fas fa-exclamation-circle fa-4x mb-4"></i>
          <h2>Job Not Found</h2>
          <button onClick={() => navigate('/careers')} className="btn btn-primary mt-3">
            Browse All Jobs
          </button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO title={`${job.title} - Careers at Supernova Lab`} />
      
      <div className="job-detail-page">
        {/* Job Header */}
        <section className="job-header">
          <div className="container">
            <button onClick={() => navigate('/careers')} className="back-btn">
              <i className="fas fa-arrow-left me-2"></i>Back to All Jobs
            </button>

            <div className="job-header-content">
              <div>
                {/* Only show featured badge if featured is true/1 */}
                {job.featured === 1 && (
                  <span className="featured-badge-large">
                    <i className="fas fa-star me-2"></i>Featured Position
                  </span>
                )}
                <h1 className="job-detail-title">{job.title}</h1>
                
                <div className="job-meta-large">
                  <span><i className="fas fa-map-marker-alt"></i> {job.location}</span>
                  <span><i className="fas fa-briefcase"></i> {job.employment_type}</span>
                  <span><i className="fas fa-layer-group"></i> {job.experience_level}</span>
                  {/* Only show remote if remote_allowed is true/1 */}
                  {job.remote_allowed === 1 && (
                    <span className="remote"><i className="fas fa-laptop-house"></i> Remote Friendly</span>
                  )}
                </div>

                {job.salary_range && (
                  <div className="salary-display">
                    <i className="fas fa-dollar-sign me-2"></i>
                    <strong>{job.salary_range}</strong>
                  </div>
                )}
              </div>

              <button onClick={() => setShowModal(true)} className="apply-btn-large">
                <i className="fas fa-paper-plane me-2"></i>Apply Now
              </button>
            </div>
          </div>
        </section>

        {/* Job Content */}
        <section className="job-content">
          <div className="container">
            <div className="content-section">
              <h2>About This Role</h2>
              <div className="job-description" dangerouslySetInnerHTML={{ __html: job.description }} />
            </div>

            <div className="content-section">
              <h2>Responsibilities</h2>
              <div dangerouslySetInnerHTML={{ __html: job.responsibilities }} />
            </div>

            <div className="content-section">
              <h2>Requirements</h2>
              <div dangerouslySetInnerHTML={{ __html: job.requirements }} />
            </div>

            {job.benefits && (
              <div className="content-section">
                <h2>Benefits & Perks</h2>
                <div dangerouslySetInnerHTML={{ __html: job.benefits }} />
              </div>
            )}
          </div>
        </section>

        {/* Application Modal */}
        {showModal && (
          <div className="application-modal-overlay" onClick={() => !submitting && setShowModal(false)}>
            <motion.div
              className="application-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {success ? (
                <div className="success-message">
                  <i className="fas fa-check-circle fa-4x mb-4"></i>
                  <h2>Application Submitted!</h2>
                  <p>Thank you for applying. We'll review your application and get back to you soon.</p>
                </div>
              ) : (
                <>
                  <div className="modal-header">
                    <h2>Apply for {job.title}</h2>
                    <button
                      onClick={() => setShowModal(false)}
                      className="close-btn"
                      disabled={submitting}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="application-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          className={errors.full_name ? 'error' : ''}
                        />
                        {errors.full_name && <span className="error-text">{errors.full_name}</span>}
                      </div>

                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={errors.email ? 'error' : ''}
                        />
                        {errors.email && <span className="error-text">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={errors.phone ? 'error' : ''}
                        />
                        {errors.phone && <span className="error-text">{errors.phone}</span>}
                      </div>

                      <div className="form-group">
                        <label>Location</label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="City, Country"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>LinkedIn URL</label>
                        <input
                          type="url"
                          name="linkedin_url"
                          value={formData.linkedin_url}
                          onChange={handleInputChange}
                          placeholder="https://linkedin.com/in/..."
                        />
                      </div>

                      <div className="form-group">
                        <label>Portfolio URL</label>
                        <input
                          type="url"
                          name="portfolio_url"
                          value={formData.portfolio_url}
                          onChange={handleInputChange}
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Current Position</label>
                        <input
                          type="text"
                          name="current_position"
                          value={formData.current_position}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Current Company</label>
                        <input
                          type="text"
                          name="current_company"
                          value={formData.current_company}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Years of Experience</label>
                        <input
                          type="number"
                          name="years_of_experience"
                          value={formData.years_of_experience}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Expected Salary</label>
                        <input
                          type="text"
                          name="expected_salary"
                          value={formData.expected_salary}
                          onChange={handleInputChange}
                          placeholder="e.g., $60k-$80k"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Availability</label>
                      <input
                        type="text"
                        name="availability"
                        value={formData.availability}
                        onChange={handleInputChange}
                        placeholder="e.g., Immediate, 2 weeks notice"
                      />
                    </div>

                    {/* Resume Upload */}
                    <div className="form-group">
                      <label>Resume/CV (PDF or Word, Max 5MB)</label>
                      <div className="file-upload-wrapper">
                        <input
                          type="file"
                          id="resume"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="file-input"
                        />
                        <label htmlFor="resume" className="file-label">
                          <i className="fas fa-upload me-2"></i>
                          {resumeFile ? resumeFile.name : 'Choose File'}
                        </label>
                      </div>
                      {errors.resume && <span className="error-text">{errors.resume}</span>}
                      {resumeFile && (
                        <div className="file-info">
                          <i className="fas fa-file-pdf me-2"></i>
                          {resumeFile.name} ({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Cover Letter *</label>
                      <textarea
                        name="cover_letter"
                        value={formData.cover_letter}
                        onChange={handleInputChange}
                        rows="6"
                        className={errors.cover_letter ? 'error' : ''}
                        placeholder="Tell us why you're interested in this position..."
                      />
                      {errors.cover_letter && <span className="error-text">{errors.cover_letter}</span>}
                    </div>

                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Submit Application
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default JobDetailPage;